/**
 * Calendar Page — Month view with event management
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '../../auth';
import './CalendarPage.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_SETTINGS = {
  timezone: 'America/Los_Angeles',
  slotDuration: 30,
  bufferMinutes: 15,
  bookingEnabled: false,
  bookingTitle: '',
  bookingDescription: '',
  bookingSlug: ''
};

const CalendarPage = ({ user }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({ summary: '', dtstart: '', dtend: '', location: '', description: '', allDay: false, generateMeetingLink: false, meetingLink: '' });
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('calendar'); // calendar | bookings | settings
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const bookingSlug = settings?.bookingSlug || user?.email?.split('@')[0] || '';
  const appBaseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://app.numfree.com';
  const appHost = typeof window !== 'undefined' ? window.location.host : 'app.numfree.com';
  const bookingPageUrl = `${appBaseUrl}/meet/${bookingSlug}`;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    loadEvents();
    loadBookings();
    loadSettings();
  }, []);

  useEffect(() => { loadEvents(); }, [year, month]);

  const loadEvents = async () => {
    const start = new Date(year, month, 1).toISOString();
    const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
    try {
      const res = await authFetch(`/api/calendar/events?start=${start}&end=${end}`);
      if (res.ok) setEvents(await res.json());
    } catch (e) {}
  };

  const loadBookings = async () => {
    try {
      const res = await authFetch('/api/calendar/bookings');
      if (res.ok) setBookings(await res.json());
    } catch (e) {}
  };

  const loadSettings = async () => {
    try {
      const res = await authFetch('/api/calendar/settings');
      if (res.ok) setSettings({ ...DEFAULT_SETTINGS, ...(await res.json() || {}) });
    } catch (e) {}
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toISOString().slice(0, 10);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => (e.dtstart || '').slice(0, 10) === dateStr);
  };

  const openNewEvent = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setForm({ summary: '', dtstart: dateStr + 'T09:00', dtend: dateStr + 'T09:30', location: '', description: '', allDay: false, generateMeetingLink: false, meetingLink: '' });
    setEditEvent(null);
    setSelectedDate(day);
    setShowModal(true);
  };

  const openEditEvent = (ev) => {
    setForm({
      summary: ev.summary || '', dtstart: (ev.dtstart || '').slice(0, 16), dtend: (ev.dtend || '').slice(0, 16),
      location: ev.location || '', description: ev.description || '', allDay: ev.allDay || false,
      generateMeetingLink: !!ev.meetingLink, meetingLink: ev.meetingLink || ''
    });
    setEditEvent(ev);
    setShowModal(true);
  };

  const saveEvent = async () => {
    setSaving(true);
    try {
      if (editEvent) {
        const res = await authFetch(`/api/calendar/events/${editEvent.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'If-Match': `"${editEvent.etag}"` },
          body: JSON.stringify({
            ...form,
            generateMeetingLink: form.generateMeetingLink || !!form.meetingLink,
            dtstart: new Date(form.dtstart).toISOString(),
            dtend: new Date(form.dtend).toISOString()
          })
        });
        if (!res.ok) { const d = await res.json(); alert(d.error); }
      } else {
        const res = await authFetch('/api/calendar/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            generateMeetingLink: form.generateMeetingLink,
            dtstart: new Date(form.dtstart).toISOString(),
            dtend: new Date(form.dtend).toISOString()
          })
        });
        if (!res.ok) { const d = await res.json(); alert(d.error); }
      }
      setShowModal(false);
      loadEvents();
    } catch (e) { alert('Failed to save'); }
    setSaving(false);
  };

  const deleteEvent = async () => {
    if (!editEvent || !window.confirm('Delete this event?')) return;
    await authFetch(`/api/calendar/events/${editEvent.uid}`, {
      method: 'DELETE',
      headers: { 'If-Match': `"${editEvent.etag}"` }
    });
    setShowModal(false);
    loadEvents();
  };

  const saveSettings = async () => {
    setSaving(true);
    await authFetch('/api/calendar/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    });
    setSaving(false);
  };

  const updateBookingStatus = async (bookingId, status) => {
    await authFetch(`/api/calendar/bookings/${bookingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    loadBookings();
    loadEvents();
  };

  return (
    <div className="calendar-page">
      <div className="calendar-tabs">
        <button className={tab === 'calendar' ? 'active' : ''} onClick={() => setTab('calendar')}>Calendar</button>
        <button className={tab === 'bookings' ? 'active' : ''} onClick={() => setTab('bookings')}>
          Bookings {bookings.filter(b => b.status === 'confirmed').length > 0 && <span className="badge">{bookings.filter(b => b.status === 'confirmed').length}</span>}
        </button>
        <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>Settings</button>
      </div>

      {tab === 'calendar' && (
        <div className="calendar-view">
          <div className="calendar-nav">
            <button onClick={prevMonth}>&lt;</button>
            <h2>{MONTHS[month]} {year}</h2>
            <button onClick={nextMonth}>&gt;</button>
            <button className="btn-today" onClick={today}>Today</button>
          </div>

          <div className="calendar-grid">
            {DAYS.map(d => <div key={d} className="cal-header">{d}</div>)}
            {Array.from({ length: firstDay }, (_, i) => <div key={'e' + i} className="cal-cell empty" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === todayStr;
              const dayEvents = getEventsForDay(day);
              return (
                <div key={day} className={`cal-cell ${isToday ? 'today' : ''}`} onClick={() => openNewEvent(day)}>
                  <span className="cal-day-num">{day}</span>
                  {dayEvents.slice(0, 3).map(ev => (
                    <div key={ev.uid} className="cal-event" style={{ backgroundColor: ev.color || '#2E7D86' }}
                      onClick={e => { e.stopPropagation(); openEditEvent(ev); }}>
                      {ev.summary}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="cal-more">+{dayEvents.length - 3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'bookings' && (
        <div className="bookings-view">
          <h3>Incoming Bookings</h3>
          {settings.bookingEnabled && (
            <div className="booking-url-bar">
              <span className="booking-url-label">Your booking page:</span>
              <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer" className="booking-url-link">
                {appHost}/meet/{bookingSlug || '...'}
              </a>
              <button className="btn-copy-url" onClick={() => {
                navigator.clipboard.writeText(bookingPageUrl);
              }} title="Copy link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
          )}
          {bookings.length === 0 ? (
            <p className="empty-msg">{settings.bookingEnabled ? 'No bookings yet. Share your booking link to get started!' : 'Enable your booking page in Settings first.'}</p>
          ) : (
            <div className="bookings-list">
              {bookings.map(b => (
                <div key={b.bookingId} className={`booking-card ${b.status}`}>
                  <div className="booking-info">
                    <strong>{b.guestName}</strong> — {b.guestEmail}
                    <div className="booking-time">{new Date(b.slot).toLocaleString()} ({b.duration}min)</div>
                    {b.notes && <div className="booking-notes">{b.notes}</div>}
                  </div>
                  <div className="booking-actions">
                    <span className={`booking-status ${b.status}`}>{b.status}</span>
                    {b.status === 'confirmed' && (
                      <>
                        <button className="btn-cancel-booking" onClick={() => updateBookingStatus(b.bookingId, 'cancelled')}>Cancel</button>
                        <button className="btn-noshow" onClick={() => updateBookingStatus(b.bookingId, 'no_show')}>No-show</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div className="settings-view">
          <h3>Calendar Settings</h3>
          <div className="form-group">
            <label>Timezone</label>
            <select value={settings.timezone || ''} onChange={e => setSettings({ ...settings, timezone: e.target.value })}>
              {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
                'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney',
                'Pacific/Auckland', 'UTC'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Slot Duration (minutes)</label>
            <select value={settings.slotDuration || 30} onChange={e => setSettings({ ...settings, slotDuration: parseInt(e.target.value) })}>
              {[15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Buffer Between Meetings (minutes)</label>
            <select value={settings.bufferMinutes || 15} onChange={e => setSettings({ ...settings, bufferMinutes: parseInt(e.target.value) })}>
              {[0, 5, 10, 15, 30].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>
              <input type="checkbox" checked={settings.bookingEnabled || false} onChange={e => setSettings({ ...settings, bookingEnabled: e.target.checked })} />
              {' '}Enable public booking page
            </label>
          </div>
          {settings.bookingEnabled && (
            <>
              <div className="form-group">
                <label>Booking Page Title</label>
                <input value={settings.bookingTitle || ''} onChange={e => setSettings({ ...settings, bookingTitle: e.target.value })} placeholder="Book a meeting" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={settings.bookingDescription || ''} onChange={e => setSettings({ ...settings, bookingDescription: e.target.value })} rows={3} />
              </div>
              <div className="form-group">
                <label>Booking URL slug</label>
                <div className="slug-preview">
                  {appHost}/meet/<input value={settings.bookingSlug || ''} onChange={e => setSettings({ ...settings, bookingSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder={user?.email?.split('@')[0] || 'yourname'} />
                </div>
                <div className="booking-share-url">
                  <span>Share this link:</span>
                  <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer">
                    {bookingPageUrl}
                  </a>
                </div>
              </div>
            </>
          )}
          <button className="btn-save" onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
        </div>
      )}

      {/* Event Modal */}
      {showModal && (
        <div className="calendar-modal modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editEvent ? 'Edit Event' : 'New Event'}</h3>
            <div className="form-group">
              <label>Title</label>
              <input value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} autoFocus />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start</label>
                <input type="datetime-local" value={form.dtstart} onChange={e => setForm({ ...form, dtstart: e.target.value })} />
              </div>
              <div className="form-group">
                <label>End</label>
                <input type="datetime-local" value={form.dtend} onChange={e => setForm({ ...form, dtend: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Zoom, office, etc." />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!form.generateMeetingLink}
                  onChange={e => setForm({ ...form, generateMeetingLink: e.target.checked || !!form.meetingLink })}
                  disabled={!!form.meetingLink}
                />
                {' '}Add NumFree video link
              </label>
              {form.meetingLink ? (
                <div className="booking-share-url" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <a href={form.meetingLink} target="_blank" rel="noopener noreferrer">Join video meeting →</a>
                  <button type="button" className="btn-copy-url" title="Copy link"
                    onClick={() => navigator.clipboard.writeText(form.meetingLink)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                  </button>
                </div>
              ) : null}
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="modal-actions">
              {editEvent && <button className="btn-delete-event" onClick={deleteEvent}>Delete</button>}
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-save" onClick={saveEvent} disabled={saving || !form.summary}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
